import React from 'react';
import { Home, Radio, Users, Compass, Bookmark, User, PlusCircle, Bell, Shield, Layers, Globe, Settings, Megaphone, BarChart3 } from 'lucide-react';
import { cn } from '../utils';

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActivities } from '../context/ActivitiesContext';
import { canManagePlatform } from '../utils/permissions';

const SidebarItem = ({ icon: Icon, label, to, badge }) => {
  const location = useLocation();
  const active = to === '/' 
    ? location.pathname === '/' 
    : location.pathname.startsWith(to);
  
  return (
    <Link to={to} className={cn(
      "flex items-center gap-4 px-4 py-3 cursor-pointer rounded-2xl transition-all duration-300 group",
      active ? "bg-brand-indigo/10 text-brand-indigo shadow-sm" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    )}>
      <div className={cn(
        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300",
        active ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-gray-600 group-hover:shadow-sm"
      )}>
        <Icon size={20} className={cn("transition-transform duration-300", active ? "scale-110" : "group-hover:scale-110")} />
      </div>
      <span className={cn(
        "font-bold text-sm tracking-tight transition-colors duration-300",
        active ? "text-brand-indigo" : "text-gray-600 group-hover:text-gray-900"
      )}>
        {label}
      </span>
      {badge && (
        <span className="ml-auto bg-brand-accent text-white text-[10px] font-bold px-2 py-0.5 rounded-lg min-w-[20px] text-center shadow-sm uppercase tracking-[0.15em]">
          {badge}
        </span>
      )}
    </Link>
  );
};

const Sidebar = ({ onCreateClick }) => {
  const { user } = useAuth();
  const { rooms } = useActivities();
  const liveCount = (Array.isArray(rooms) ? rooms : []).filter(room => room.status === 'Live').length;
  const isPlatformAdmin = canManagePlatform(user);

  return (
    <aside className="w-72 h-screen sticky top-0 bg-white border-r border-gray-100 flex flex-col py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent z-[50]">
      <div className="px-6 mb-8 flex items-center justify-between">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">Menu</h3>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {isPlatformAdmin ? (
          <>
            <SidebarItem icon={Shield} label="Admin Dashboard" to="/admin" />
            <SidebarItem icon={Users} label="Manage Users" to="/admin/users" />
            <SidebarItem icon={Shield} label="Moderation" to="/admin/moderation" badge="14" />
            <SidebarItem icon={Layers} label="Manage Activities" to="/admin/activities" />
            <SidebarItem icon={Globe} label="Manage Communities" to="/admin/communities" />
            <SidebarItem icon={Megaphone} label="Announcements" to="/admin/announcements" />
            <SidebarItem icon={Settings} label="Platform Settings" to="/admin/settings" />
            <SidebarItem icon={BarChart3} label="App Analytics" to="/admin/analytics" />
          </>
        ) : (
          <>
            <SidebarItem icon={Home} label="Home" to="/" />
            <SidebarItem icon={Radio} label="Live" to="/live" badge={liveCount > 0 ? liveCount : undefined} />
            <SidebarItem icon={Users} label="Communities" to="/communities" />
            <SidebarItem icon={Compass} label="Explore" to="/explore" />
            <SidebarItem icon={Bookmark} label="Saved" to="/saved" />
            <SidebarItem icon={User} label="Profile" to="/profile" />
            <SidebarItem icon={Settings} label="Settings" to="/settings" />
          </>
        )}
      </nav>

      {!isPlatformAdmin && (
        <div className="px-4 mt-auto mb-4">
          <button 
            onClick={onCreateClick}
            className="w-full bg-brand-indigo hover:bg-brand-indigo/90 text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center gap-3 transition-all duration-300 shadow-xl shadow-brand-indigo/20 active:scale-[0.96] group overflow-hidden relative"
          >
            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <PlusCircle size={22} className="relative z-10 group-hover:rotate-12 transition-transform duration-300" />
            <span className="relative z-10 tracking-tight">Quick Create</span>
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
